/**
 * The Restoration Project (RPU) — BGforge's maintained build of killap's
 * Fallout 2 Restoration Project, installed the same way everything else here
 * is: the player brings the release archive, we never host game content.
 *
 * ## What the release actually is
 *
 * `rpu_v*.zip` from BGforgeNet/Fallout2_Restoration_Project unpacks over a
 * desktop game directory: `mods/*.dat` (rpu.dat plus a set of optional
 * companion mods), `mods/*.ini` (their settings), a root `mods_order.txt`, a
 * root sfall global script (`gl_ammomod.int`), and a pile of Windows loader
 * files we quarantine on principle. The engine's sfall mods layer
 * (engine/src/sfall_ext.cc) reads `mods/mods_order.txt` and stacks each listed
 * `mods/<name>.dat` above patch000.dat — exactly sfall's semantics.
 *
 * ## Why installing is *surgery*, not just extraction
 *
 * The authority for a correct install is the release's own `rpu-install.sh`,
 * and it does more than copy files in:
 *
 *   - `patch000.dat` and `f2_res.dat` are REMOVED. rpu.dat carries the
 *     Unofficial Patch's fixes itself and expects to sit on a bare master.dat;
 *     leaving 1.02 underneath is an unsupported hybrid.
 *   - loose `data/` from the old install is cleared, keeping only savegames
 *     (not in our vault anyway), `data/*.txt`, `data/*.gcd`, and
 *     `data/sound/music/`. Anything else — protos, maps, scripts from a
 *     desktop install — would shadow RPU's copies from the highest-priority
 *     xbase and corrupt the mod invisibly.
 *   - root `mods_order.txt` moves to `mods/mods_order.txt`, and the loose
 *     sfall global script moves to `data/scripts/`.
 *
 * We reproduce all of that against the vault. MEMFS is rebuilt from the vault
 * on every boot, so this runs once here rather than per-boot.
 *
 * ## Saves
 *
 * RPU must start from a new game. The engine will happily *load* a vanilla
 * save, but killap's own releases are emphatic that mid-game installs
 * misbehave (missing map state, broken scripts), so the panel says it plainly
 * rather than letting the player discover it in Klamath.
 */

import { readRegistry, registerGame } from '../registry';
import { deleteFiles, getMeta, listFiles, putFiles, setMeta, type InstallMeta, type VaultEntry } from '../store';
import { entryBlob, readCentralDirectory, ZipReadError, type ZipEntry } from '../zip/read';
import { DatError, readDat2Info } from './dat';
import type { ImportProgress } from './index';

export class RpuError extends Error {}

/** Stored under meta key 'rpu' while a Restoration Project install is live. */
export interface RpuMeta {
  version: string;
  installedAt: number;
  fileCount: number;
  totalBytes: number;
  /** The mods_order.txt entries that actually exist in the vault, in order. */
  mods: string[];
}

export const RPU_META_KEY = 'rpu';

export const RPU_RELEASES_URL = 'https://github.com/BGforgeNet/Fallout2_Restoration_Project/releases/latest';

export interface RpuInstallResult {
  meta: RpuMeta;
  warnings: string[];
  /** Vault paths removed by the install-script surgery. */
  removed: string[];
}

export async function getRpuMeta(): Promise<RpuMeta | undefined> {
  return getMeta<RpuMeta>(RPU_META_KEY);
}

/** "rpu_v2.4.34.zip" → "2.4.34". Renamed downloads ("rpu_v2.4.34 (1).zip") miss. */
function versionFromName(name: string): string {
  const m = /rpu[_-]v?([0-9][\w.]*)\.zip$/i.exec(name);
  return m ? m[1] : 'unknown';
}

/**
 * The archive's own answer: the build stamps
 * `VersionString=FALLOUT II 1.02d  RPU <version>` into ddraw.ini
 * (extra/package/sfall.sh in the RPU repo), which survives whatever the
 * browser renamed the download to.
 */
function versionFromDdrawIni(text: string | null): string | null {
  if (!text) return null;
  // Older builds stamp "RP <version>", newer ones "RPU <version>".
  const m = /^\s*VersionString\s*=.*?RP[U]?\s+v?([\d][\d.]*)/im.exec(text);
  return m ? m[1] : null;
}

/**
 * The main menu's corner stamp is this string, and RPU's own "FALLOUT II
 * 1.02d  RP 2.4.34" reads as line noise to anyone who doesn't already know
 * the mod's initials. Spell it out instead — that stamp is the one always-on
 * indicator that the mod is live.
 *
 * The length is a hard constraint, not taste: the engine renders it through a
 * `char[VERSION_MAX]` with VERSION_MAX = 32 (engine/src/version.h), so
 * anything longer is silently cut mid-word. "RESTORATION PROJECT 2.4.34" is
 * 26 characters; the FALLOUT II prefix is what gets sacrificed, on a screen
 * that is not short of Fallout branding.
 */
function brandVersionString(text: string, version: string | null): string {
  const stamp = version ? `RESTORATION PROJECT ${version}` : 'RESTORATION PROJECT';
  return text.replace(/^(\s*VersionString\s*=)[^\r\n]*/im, `$1${stamp}`);
}

/**
 * The entry that proves this is the RPU manual archive, and the anchor the
 * root prefix is measured from — same trick as stripRoot() over master.dat,
 * because people re-zip folders.
 */
function findRpuDat(entries: ZipEntry[]): ZipEntry | undefined {
  const candidates = entries.filter((e) => !e.isDirectory && /(^|\/)mods\/rpu\.dat$/i.test(e.name));
  candidates.sort((a, b) => a.name.split('/').length - b.name.split('/').length);
  return candidates[0];
}

interface PlannedFile {
  entry: ZipEntry;
  /** Vault path (game-root relative, forward slashes). */
  path: string;
}

/**
 * Maps archive entries to vault paths, applying the install script's moves.
 * Everything not explicitly wanted is left in the zip: exes, dlls, launcher
 * scripts, legacy sfall/Hi-Res config, readmes.
 */
function planFiles(entries: ZipEntry[], prefix: string): PlannedFile[] {
  const planned: PlannedFile[] = [];
  for (const e of entries) {
    if (e.isDirectory || !e.name.startsWith(prefix)) continue;
    const rel = e.name.slice(prefix.length);
    const lower = rel.toLowerCase();
    if (/^mods\/[^/]+\.(dat|ini)$/.test(lower) || lower === 'mods/mods_order.txt') {
      planned.push({ entry: e, path: `mods/${rel.slice('mods/'.length)}` });
    } else if (lower === 'mods_order.txt') {
      // rpu-install.sh: `mv -f mods_order.txt mods/mods_order.txt`
      planned.push({ entry: e, path: 'mods/mods_order.txt' });
    } else if (/^gl_[^/]+\.int$/.test(lower)) {
      // rpu-install.sh: `mv gl_ammomod.int data/scripts/`
      planned.push({ entry: e, path: `data/scripts/${rel}` });
    } else if (lower === 'ddraw.ini') {
      // The one loader-config exception. RPU's ddraw.ini is not leftover sfall
      // configuration, it is part of the mod: its scripts hard-fail unless
      // `WorldMapSlots=21` reads back from it, and `IniConfigFolder=mods` is
      // how those scripts find mods/rpu.ini at all. The engine parses the
      // sfall keys it implements (engine/src/sfall_config.cc) and serves
      // script-side get_ini_setting from the same file (sfall_ini.cc).
      planned.push({ entry: e, path: 'ddraw.ini' });
    }
  }
  // A root mods_order.txt and a mods/mods_order.txt in the same archive both
  // land on the same vault path. The install script backs the mods/ one away
  // and `mv -f`s the root copy over it, so the root copy is the one a desktop
  // install ends up with; keep only it.
  const orderCopies = planned.filter((f) => f.path === 'mods/mods_order.txt');
  if (orderCopies.length > 1) {
    const root = orderCopies.find((f) => !/(^|\/)mods\/mods_order\.txt$/i.test(f.entry.name)) ?? orderCopies[0];
    return planned.filter((f) => f.path !== 'mods/mods_order.txt' || f === root);
  }
  return planned;
}

/**
 * Which vault paths the install script would sweep away.
 *
 * Mirrors rpu-install.sh: patch000.dat and f2_res.dat go; loose data/ goes
 * except `data/*.txt`, `data/*.gcd` and `data/sound/music/`. Savegames never
 * enter the vault, and `data/scripts/gl_*.int` is what we are installing.
 */
export function isSweptByRpu(path: string): boolean {
  const p = path.toLowerCase();
  if (p === 'patch000.dat' || p === 'f2_res.dat') return true;
  if (!p.startsWith('data/')) return false;
  if (/^data\/[^/]+\.(txt|gcd)$/.test(p)) return false;
  if (p.startsWith('data/sound/music/')) return false;
  if (/^data\/scripts\/gl_[^/]+\.int$/.test(p)) return false;
  return true;
}

const BATCH_BYTES = 48 * 1024 * 1024;

export async function installRpu(
  file: File,
  onProgress: (p: ImportProgress) => void
): Promise<RpuInstallResult> {
  if (!(await getMeta<InstallMeta>('install'))) {
    throw new RpuError('Import your Fallout 2 game data first — the Restoration Project installs on top of it.');
  }
  if (/\.exe$/i.test(file.name)) {
    throw new RpuError(
      `${file.name} is the Windows installer, which nothing in a browser can run. ` +
        'Download the manual archive instead — the release page has an rpu_v*.zip right next to it.'
    );
  }

  let entries: ZipEntry[];
  try {
    entries = await readCentralDirectory(file);
  } catch (e) {
    if (e instanceof ZipReadError) {
      throw new RpuError(`${e.message} The Restoration Project ships as rpu_v*.zip; drop that file whole.`);
    }
    throw e;
  }

  const anchor = findRpuDat(entries);
  if (!anchor) {
    const names = entries.filter((e) => !e.isDirectory).slice(0, 8).map((e) => e.name);
    const looksLikeTranslations = entries.some((e) => /(^|\/)mods\/rpu_[a-z]+\.dat$/i.test(e.name));
    throw new RpuError(
      `${file.name} does not contain mods/rpu.dat. It holds: ${names.join(', ')}${entries.length > 8 ? ', …' : ''}. ` +
        (looksLikeTranslations
          ? 'That looks like the translations add-on — install the main rpu_v*.zip first.'
          : 'You want the rpu_v*.zip manual archive from the release page, dropped whole.')
    );
  }
  const prefix = anchor.name.slice(0, anchor.name.toLowerCase().lastIndexOf('mods/rpu.dat'));

  const planned = planFiles(entries, prefix);
  const warnings: string[] = [];

  // Prove the payload before touching the vault: rpu.dat has to be a real
  // DAT2, and a truncated download fails here rather than as a boot failure.
  onProgress({ stage: 'Checking', file: 'mods/rpu.dat', bytesDone: 0, bytesTotal: 0, filesDone: 0, filesTotal: planned.length });
  const rpuBlob = await entryBlob(file, anchor);
  try {
    await readDat2Info(rpuBlob);
  } catch (e) {
    if (e instanceof DatError) {
      throw new RpuError(`The rpu.dat inside ${file.name} failed its structure check: ${e.message}`);
    }
    throw e;
  }

  // The load order file is the contract the engine reads. The release always
  // carries one; synthesizing on absence keeps a hand-repacked archive usable.
  let orderText: string;
  const orderPlan = planned.find((f) => f.path === 'mods/mods_order.txt');
  if (orderPlan) {
    orderText = new TextDecoder().decode(await (await entryBlob(file, orderPlan.entry)).arrayBuffer());
  } else {
    const dats = planned.filter((f) => /^mods\/.+\.dat$/i.test(f.path)).map((f) => f.path.slice('mods/'.length));
    dats.sort((a, b) => (a.toLowerCase() === 'rpu.dat' ? -1 : 0) - (b.toLowerCase() === 'rpu.dat' ? -1 : 0));
    orderText = dats.join('\n') + '\n';
    warnings.push('The archive had no mods_order.txt; generated one with rpu.dat first.');
  }

  const ddrawPlan = planned.find((f) => f.path === 'ddraw.ini');
  const ddrawText = ddrawPlan
    ? new TextDecoder().decode(await (await entryBlob(file, ddrawPlan.entry)).arrayBuffer())
    : null;
  const version = versionFromDdrawIni(ddrawText) ?? versionFromName(file.name);
  const brandedDdraw = ddrawText ? brandVersionString(ddrawText, version === 'unknown' ? null : version) : null;

  // --- copy in, batched like ingest() ---------------------------------------
  const bytesTotal = planned.reduce((n, f) => n + f.entry.uncompressedSize, 0);
  let bytesDone = 0;
  let filesDone = 0;
  let batch: VaultEntry[] = [];
  let batchBytes = 0;
  const flush = async () => {
    if (!batch.length) return;
    await putFiles(batch);
    batch = [];
    batchBytes = 0;
  };

  for (const f of planned) {
    onProgress({ stage: 'Copying', file: f.path, bytesDone, bytesTotal, filesDone, filesTotal: planned.length });
    const blob =
      f.path === 'mods/mods_order.txt'
        ? new Blob([orderText])
        : f.path === 'ddraw.ini' && brandedDdraw !== null
          ? new Blob([brandedDdraw])
          : f.entry === anchor
            ? rpuBlob
            : await entryBlob(file, f.entry);
    batch.push({ path: f.path, size: blob.size, blob });
    batchBytes += blob.size;
    bytesDone += f.entry.uncompressedSize;
    filesDone++;
    if (batchBytes >= BATCH_BYTES) {
      onProgress({ stage: 'Saving', file: f.path, bytesDone, bytesTotal, filesDone, filesTotal: planned.length });
      await flush();
    }
  }
  if (!orderPlan) {
    batch.push({ path: 'mods/mods_order.txt', size: orderText.length, blob: new Blob([orderText]) });
  }
  await flush();

  // --- the install script's surgery -----------------------------------------
  onProgress({ stage: 'Removing superseded files', bytesDone, bytesTotal, filesDone, filesTotal: planned.length });
  const before = await listFiles();
  const installedPaths = new Set(planned.map((f) => f.path));
  const removed = before.map((f) => f.path).filter((p) => !installedPaths.has(p) && isSweptByRpu(p));
  await deleteFiles(removed);

  // --- bookkeeping -----------------------------------------------------------
  const after = await listFiles();
  const names = new Set(after.map((f) => f.path.toLowerCase()));
  const orderedMods = orderText
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !/[;#]/.test(l))
    .filter((l) => names.has(`mods/${l.toLowerCase()}`));

  const rpuMeta: RpuMeta = {
    version,
    installedAt: Date.now(),
    fileCount: planned.length,
    totalBytes: bytesTotal,
    mods: orderedMods,
  };
  await setMeta(RPU_META_KEY, rpuMeta);

  const install = await getMeta<InstallMeta>('install');
  if (install) {
    await setMeta('install', {
      ...install,
      fileCount: after.length,
      totalBytes: after.reduce((n, f) => n + f.size, 0),
      hasPatch: names.has('patch000.dat'),
      dats: after.filter((f) => /^[^/]+\.dat$/i.test(f.path)).map((f) => f.path),
    } satisfies InstallMeta);
  }

  const fo2Entry = readRegistry().find((e) => e.gameType === 'fo2');
  if (fo2Entry) {
    registerGame({ ...fo2Entry, hasRpu: true, rpuVersion: version === 'unknown' ? undefined : version });
  }

  onProgress({ stage: 'Done', bytesDone: bytesTotal, bytesTotal, filesDone: planned.length, filesTotal: planned.length });
  return { meta: rpuMeta, warnings, removed };
}

/**
 * Removes every RPU-installed file. Deliberately conservative about what it
 * cannot restore: patch000.dat and any swept loose data are *gone*, so the
 * caller must say that the way back to 1.02 is re-adding the patch (or
 * re-importing), not pretend this is a clean toggle.
 */
export async function uninstallRpu(): Promise<string[]> {
  const doomed = (await listFiles())
    .map((f) => f.path)
    .filter((p) => /^mods\//i.test(p) || /^data\/scripts\/gl_[^/]+\.int$/i.test(p) || p.toLowerCase() === 'ddraw.ini');
  await deleteFiles(doomed);
  await setMeta(RPU_META_KEY, undefined);
  const fo2Entry = readRegistry().find((e) => e.gameType === 'fo2');
  if (fo2Entry) {
    registerGame({ ...fo2Entry, hasRpu: false, rpuVersion: undefined });
  }

  const after = await listFiles();
  const names = new Set(after.map((f) => f.path.toLowerCase()));
  const install = await getMeta<InstallMeta>('install');
  if (install) {
    await setMeta('install', {
      ...install,
      fileCount: after.length,
      totalBytes: after.reduce((n, f) => n + f.size, 0),
      hasPatch: names.has('patch000.dat'),
      dats: after.filter((f) => /^[^/]+\.dat$/i.test(f.path)).map((f) => f.path),
    } satisfies InstallMeta);
  }
  return doomed;
}
